import { useCallback, useState, useRef } from 'react';
import { VoiceInterface, type VoiceInterfaceHandle } from '@frontend/ai-receptionist-ui';
import { getJTI } from '@frontend/auth-helpers';
import { getInitialParams } from '@frontend/env';
import { http } from '@frontend/fetch';
import { useTranslation } from '@frontend/i18n';
import { useChannelSubscription } from '@frontend/queue';
import { useAppScopeStore } from '@frontend/scope';
import {
  GlobalSettingsPanelEntry,
  SettingsPanelHeader,
  SettingsPanelContent,
  useGlobalSettingsPanel,
} from '@frontend/settings-panel-engine';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';

export const AiReceptionistGlobalPanel = () => {
  const { open, close } = useGlobalSettingsPanel('ai-receptionist-test-panel');
  const { t } = useTranslation('ai-receptionist');
  const [settingsBanner, setSettingsBanner] = useState<string | null>(null);
  const voiceInterfaceRef = useRef<VoiceInterfaceHandle>(null);
  const token = getJTI();
  const { selectedLocationIds } = useAppScopeStore();
  const locationId = selectedLocationIds[0];
  const currentBaseUrl = (http.baseUrl || getInitialParams().backendApi).replace(/^https?/, 'wss');

  const wsUrl =
    token && locationId && currentBaseUrl
      ? `${currentBaseUrl}/v1/va-reverse-proxy/ws/connect?location_id=${locationId}&token=${token}`
      : '';

  // Subscribe to AI receptionist events
  const eventHandler = useCallback(
    (event: any) => {
      if (event.payload?.action === 'open') {
        // Clear banner when opening
        setSettingsBanner(null);
        open();
        return true;
      }
      if (event.payload?.action === 'close') {
        close();
        return true;
      }
      if (event.payload?.action === 'settings-changed') {
        // End the call and show banner
        voiceInterfaceRef.current?.endCall();
        setSettingsBanner(event.payload.message);
        return true;
      }
      return false;
    },
    [open, close]
  );

  useChannelSubscription('ai-receptionist-test-panel', eventHandler);

  if (!wsUrl) {
    return null;
  }

  return (
    <GlobalSettingsPanelEntry id='ai-receptionist-test-panel' width='medium'>
      <SettingsPanelHeader>
        <SettingsPanelHeader.Title>{t('Test AI Receptionist')}</SettingsPanelHeader.Title>
        <SettingsPanelHeader.Close />
      </SettingsPanelHeader>
      <Text color='subdued' css={{ padding: `0 ${theme.spacing(4)}` }}>
        {t(
          'This is a simulated phone call with the AI Receptionist. No real phone calls are being placed by your office.'
        )}
      </Text>
      <SettingsPanelContent css={{ height: '100%' }}>
        <VoiceInterface ref={voiceInterfaceRef} wsUrl={wsUrl} settingsChangedBanner={settingsBanner} />
      </SettingsPanelContent>
    </GlobalSettingsPanelEntry>
  );
};
